// The one check for "is this URL param safe to navigate to".
//
// Two params now carry a destination a user did not type: `?next=` (AuthGate →
// LoginScreen, after an unauthenticated load) and `?backTo=` (#1105's setup
// panel → the Pleadings editor, so closing it returns where you came from).
// Both are attacker-reachable — a link is a link — and the failure is an open
// redirect. The inputs to know about are the ones that pass a naive
// `startsWith('/')`: `//evil.example` is protocol-relative, and a backslash
// normalises to a separator so `/\evil.example` can resolve off-origin.
//
// Extracted from `LoginScreen.parseNextParam` rather than copied. A second
// implementation of a redirect guard is the kind of thing that gets hardened
// once and stays wrong in the other place.

/**
 * `raw` if it is a same-origin path, otherwise `fallback`.
 *
 * NOT decoded again: `URLSearchParams` already decodes the outer query, and
 * decoding twice corrupts destination filters and fragments, and throws on a
 * legitimate literal percent sign.
 */
export function safeInternalPath(raw: string | null | undefined, fallback: string): string {
  if (!raw?.startsWith('/') || raw.startsWith('//') || raw.split(/[?#]/, 1)[0].includes('\\')) {
    return fallback;
  }
  try {
    // THIS is the check that does the work, and the string tests above are
    // redundant with it rather than the other way round — mutation-tested:
    // deleting either one leaves every hostile input still rejected, and
    // deleting this block lets one through. `//host` resolves to a different
    // origin, and a backslash normalises to `/` so `/\host` lands a pathname
    // beginning `//`, which `navigate()` would treat as protocol-relative.
    //
    // They stay anyway, deliberately: this is a security guard, the cost is two
    // string comparisons, and they reject without depending on one `URL`
    // implementation agreeing with another. Do not read their passing tests as
    // proof they are load-bearing — see internalPath.test.ts.
    const destination = new URL(raw, window.location.origin);
    if (destination.origin !== window.location.origin
        || destination.pathname.startsWith('//')) {
      return fallback;
    }
  } catch {
    return fallback;
  }
  return raw;
}
